import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { ObservationType } from '@interfaces-clean/observations.interface';
import { useTranslation } from '@hooks/index';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';

interface Props {
    studyRef: string;
    observationId: string;
    onSubmit: (data: AddReviewLog) => void;
    onClose: () => void;
    level: ObservationType;
}

export interface AddReviewLog {
    dataset: { id: number; name: string };
    addAndReview: boolean;
}

const COMPONENT_NAME = 'add-review-log-dialog';

const AddReviewLogDialog = ({ onClose, onSubmit, studyRef, observationId, level }: Props) => {
    const { t } = useTranslation('cm');

    const { data: datasetsData, isLoading: loadingDatasets } = ObservationsApi.useGetDatasets(
        studyRef,
        observationId,
        level,
    );
    const [datasets, setDatasets] = useState<{ id: number; name: string }[]>([]);

    let dialogTitle = t('common:add');
    switch (level) {
        case ObservationType.Site:
            dialogTitle += ` ${t('reviewLogDialog.siteTitle')}`;
            break;
        case ObservationType.Study:
            dialogTitle += ` ${t('reviewLogDialog.studyTitle')}`;
            break;
        case ObservationType.Controls:
            dialogTitle += ` ${t('reviewLogDialog.controlsTitle')}`;
            break;
        default:
            dialogTitle = '';
    }

    const primaryBtnLabel = `${t('common:add')}`;
    const secondaryBtnLabel = `${t('common:cancel')}`;
    const thirdButton = `${t('reviewLogDialog.addAndReview')}`;

    useEffect(() => {
        if (datasetsData && !loadingDatasets) {
            const decisionData = datasetsData.map((data) => ({
                name: data?.datasetDate ?? '',
                id: data?.id ?? 0,
            }));

            setDatasets(decisionData);
        }
    }, [datasetsData]);

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<AddReviewLog>({
        mode: 'all',
        defaultValues: {
            dataset: undefined,
        },
    });

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    const onAddAndReviewClick = (data: AddReviewLog) => {
        data.addAndReview = true;
        return onSubmit(data);
    };

    const validatePanelHasData = (value: { id: number; name: string } | null) => {
        let validationError;
        switch (level) {
            case ObservationType.Site:
                validationError = t('reviewLogDialog.siteDatasetValidation');
                break;
            case ObservationType.Study:
                validationError = t('reviewLogDialog.studyDatasetValidation');
                break;
            case ObservationType.Controls:
                validationError = t('reviewLogDialog.controlsDatasetValidation');
                break;
            default:
                validationError = '';
        }
        if (value == null) return true;
        if (datasetsData && datasetsData.length > 0) {
            const matchingDataset = datasetsData.find((d) => d.id === value.id);

            if (level === ObservationType.Site) {
                return matchingDataset?.hasPanelData === false || matchingDataset?.isSiteActive === false
                    ? validationError
                    : true;
            }

            return matchingDataset?.hasPanelData === false ? validationError : true;
        }

        return true;
    };

    return (
        <DraggableDialog
            dataTestId="add-review-log-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            createdUpdatedBackgroundColour="gray"
            initialHeight={138}
            initialWidth={294}
            bodyPadding="none"
            setHeight="100%"
            secondaryBtnLabel={secondaryBtnLabel}
            additionalBtnDisabled={!isValid || !isDirty || isSubmitting}
            additionalBtnLabel={thirdButton}
            onAdditionalAction={() => handleSubmit(onAddAndReviewClick)()}
        >
            <form id="edit-study-review-log" className="k-w-full k-pt-sm">
                <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md k-pl-md k-pr-md">
                    <BasicDropDownController
                        label={t('reviewLogDialog.dataset')}
                        control={control}
                        name="dataset"
                        data-testid="add-review-log-dataset"
                        data={datasets}
                        textField="name"
                        dataItemKey="id"
                        filterable
                        loading={loadingDatasets}
                        allowClear
                        required
                        validateRules={{
                            checkHasPanelData: (value) => validatePanelHasData(value),
                        }}
                        readOnly={isSubmitting}
                    />
                </div>
            </form>
        </DraggableDialog>
    );
};

export default AddReviewLogDialog;
